import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, Building2, LogOut, Globe } from 'lucide-react';
import { clsx } from 'clsx';
import { LegalLinks } from './LegalLinks';
import { useAuthStore } from '../../store/authStore';
import { useNavigation } from '../../hooks/useNavigation';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  t: any;
}

export function MobileMenu({ isOpen, onClose, t }: MobileMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, clearAuth } = useAuthStore();
  const { navigationItems } = useNavigation(user);
  const [isLegalMenuOpen, setIsLegalMenuOpen] = React.useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      if (isOpen) {
        onClose();
      }
    }
  }, [location.pathname, isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLanguageChange = async (language: string) => {
    if (!user?.id) return;
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        preferredLanguage: language,
        updatedAt: new Date()
      });
      setUser({
        ...user,
        preferredLanguage: language as 'FR' | 'EN' | 'IT'
      });
    } catch (error) {
      console.error('Error updating language:', error);
    }
  };

  const handleLogout = async () => {
    try {
      if (user?.id) {
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, {
          isOnline: false,
          lastSeen: new Date()
        });
      }
      await signOut(auth);
      await clearAuth();
      onClose();
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const handleNavClick = () => {
    onClose();
  };

  return (
    <>
      <div
        className={clsx(
          'fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 md:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        style={{ zIndex: 9998 }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        className={clsx(
          'fixed inset-y-0 left-0 flex flex-col w-[85vw] max-w-sm bg-white shadow-2xl transition-transform duration-300 ease-out md:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{ zIndex: 9999 }}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-600/20">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900">Expo 1</span>
              <p className="text-xs text-slate-400">Paris 15e</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 active:bg-slate-200 transition-colors"
            aria-label="Fermer le menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <img
              className="h-10 w-10 rounded-xl object-cover"
              src={`https://ui-avatars.com/api/?name=${user?.firstName}+${user?.lastName}&background=338bff&color=fff&bold=true&format=svg`}
              alt=""
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-accent-500" />
                <span className="text-xs text-slate-400">En ligne</span>
              </div>
            </div>
            <Link
              to="/profile"
              onClick={handleNavClick}
              className="text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              Profil
            </Link>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100">
            <Globe className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <select
              value={user?.preferredLanguage || 'FR'}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="flex-1 text-sm bg-transparent border-none focus:outline-none focus:ring-0 text-slate-600 font-medium cursor-pointer"
            >
              <option value="FR">Francais</option>
              <option value="EN">English</option>
              <option value="IT">Italiano</option>
            </select>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3">
          <div className="space-y-0.5">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path === '/admin/discussion' && location.pathname.startsWith('/admin'));

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleNavClick}
                  className={clsx(
                    'group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 relative',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  )}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-600 rounded-r-full" />
                  )}
                  <item.icon
                    className={clsx(
                      'flex-shrink-0 h-5 w-5',
                      isActive ? 'text-primary-600' : 'text-slate-400'
                    )}
                  />
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <span className="flex-shrink-0 inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-xs font-bold rounded-full bg-red-500 text-white">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="flex-shrink-0 border-t border-slate-100">
          <LegalLinks
            isOpen={isLegalMenuOpen}
            setIsOpen={setIsLegalMenuOpen}
            onClose={handleNavClick}
            t={t}
          />
        </div>

        <div className="px-3 py-3 border-t border-slate-100">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
          >
            <LogOut className="h-5 w-5" />
            {t.logout}
          </button>
        </div>
      </div>
    </>
  );
}
